const writeHeader = (headerView: DataView, sampleRate: number, byteLength: number) => {
  headerView.setUint32(0, 0x52494646, false)
  headerView.setUint32(4, 44 + byteLength - 8, true)
  headerView.setUint32(8, 0x57415645, false)

  headerView.setUint32(12, 0x666d7420, false)
  headerView.setUint32(16, 16, true) // Subchunk1Size (16 for PCM)
  headerView.setUint16(20, 1, true) // AudioFormat (1 is PCM)
  headerView.setUint16(22, 1, true) // NumChannels
  headerView.setUint32(24, sampleRate, true) // SampleRate
  headerView.setUint32(28, sampleRate * 2, true) // ByteRate (SampleRate * NumChannels * BitsPerSample/8)
  headerView.setUint16(32, 2, true) // BlockAlign (NumChannels * BitsPerSample/8)
  headerView.setUint16(34, 16, true) // BitsPerSample
  headerView.setUint32(36, 0x64617461, false)
  headerView.setUint32(40, byteLength, true)
}

export const createWavArrayBuffer = (rawAudio: ArrayBuffer, sampleRate = 16000, rawLE = true) => {
  const headerSize = 44
  const file = new ArrayBuffer(headerSize + rawAudio.byteLength)
  const headerView = new DataView(file)
  const dataView = new Uint8Array(file)

  writeHeader(headerView, sampleRate, rawAudio.byteLength)

  if (rawLE) {
    dataView.set(new Uint8Array(rawAudio), headerSize)
  } else {
    const rawView = new Uint8Array(rawAudio)
    for (let i = 0, j = headerSize; i < rawAudio.byteLength; i += 2, j += 2) {
      dataView[j] = rawView[i + 1]
      dataView[j + 1] = rawView[i]
    }
  }

  return file
}

const sampleRate = (wavBuffer: ArrayBuffer) => {
  try {
    return wavBuffer.byteLength > 24 ? new DataView(wavBuffer).getUint32(24, true) : null
  } catch (e) {
    return null
  }
}

const mergePCMWaves = (...wavBuffers: ArrayBuffer[]) => {
  wavBuffers = wavBuffers.filter(sampleRate)
  const sampleRates = wavBuffers.map(sampleRate)
  if (new Set(sampleRates).size > 1) {
    throw new Error('All wav buffers must have the same sample rate')
  }

  const combinedBuffer = new Uint8Array(wavBuffers.reduce((acc, b) => acc + b.byteLength - 44, 44))
  let offset = 44
  for (const buffer of wavBuffers) {
    combinedBuffer.set(new Uint8Array(buffer, 44), offset)
    offset += buffer.byteLength - 44
  }

  const headerView = new DataView(combinedBuffer.buffer)
  writeHeader(headerView, sampleRate(wavBuffers[0]), combinedBuffer.byteLength)
  return combinedBuffer
}

export default mergePCMWaves

// 학습 오디오 레코딩 시스템 개편
export function newWriteHeader(headerView: DataView, sampleRate: number, byteLength: number) {
  // "RIFF" chunk descriptor
  headerView.setUint32(0, 0x52494646, false) // "RIFF"
  headerView.setUint32(4, 44 + byteLength - 8, true) // 전체 파일 크기 - 8
  headerView.setUint32(8, 0x57415645, false) // "WAVE"

  // "fmt " sub-chunk
  headerView.setUint32(12, 0x666d7420, false) // "fmt "
  headerView.setUint32(16, 16, true) // Subchunk1Size (16 for PCM)
  headerView.setUint16(20, 1, true) // AudioFormat (1 is PCM)
  headerView.setUint16(22, 1, true) // NumChannels (1 모노)
  headerView.setUint32(24, sampleRate, true) // SampleRate
  headerView.setUint32(28, sampleRate * 2, true) // ByteRate (샘플레이트 * 채널수 * 비트수/8)
  headerView.setUint16(32, 2, true) // BlockAlign (채널수 * 비트수/8)
  headerView.setUint16(34, 16, true) // BitsPerSample(16비트)

  // "data" sub-chunk
  headerView.setUint32(36, 0x64617461, false) // "data"
  headerView.setUint32(40, byteLength, true) // raw pcm 데이터 크기
}

export function newCreateWavArrayBuffer(pcmData: Int16Array, sampleRate: number = 44100): ArrayBuffer {
  const headerSize = 44
  const wavBuffer = new ArrayBuffer(headerSize + pcmData.byteLength)
  const headerView = new DataView(wavBuffer)

  // WAV 헤더 쓰기
  newWriteHeader(headerView, sampleRate, pcmData.byteLength)

  // 실제 PCM 데이터 복사
  const wavBytes = new Uint8Array(wavBuffer)
  wavBytes.set(new Uint8Array(pcmData.buffer), headerSize)

  return wavBuffer
}

export function newMergeFloat32ArrayToInt16(buffers: Float32Array[]): Int16Array {
  const totalLength = buffers.reduce((acc, buf) => acc + buf.length, 0)
  const totalBuffer = new Int16Array(totalLength)

  let offset = 0
  for (const buf of buffers) {
    // Float32 -> Int16 스케일링
    for (let i = 0; i < buf.length; i++) {
      // 32767 범위 내에서 잘라내기(클리핑)
      const s = Math.max(-1, Math.min(1, buf[i]))
      totalBuffer[offset++] = s < 0 ? s * 32768 : s * 32767
    }
  }
  return totalBuffer
}
